import {
  PeriodDetailsType,
  PeriodValueType,
  TemplatableActivityDateRange,
} from '@/apis/activityDateRange';
import { useDeferredRangeDateField } from '@/hooks';
import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { AccordionField } from '@components/DrawerAccordion';
import ToolTipRadioWrapper from '@components/ToolTipRadioWrapper';
import { FormControl, FormHelperText, MenuItem, RadioGroup, Stack } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TemplateCapableDatePicker from '@pages/AdvancedSearchListCreatorPage/customWidgets/templateCapableComponents/TemplateCapableDatePicker';
import TemplateCapableSelect from '@pages/AdvancedSearchListCreatorPage/customWidgets/templateCapableComponents/TemplateCapableSelectV2';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  onChange: (newValue: TemplatableActivityDateRange) => void;
  value: TemplatableActivityDateRange;
  setIsFilterInvalid: (isInvalid: boolean) => void;
  isAdvancedSearch?: boolean;
};

type ErrorType = 'invalidDate' | 'invalidRange';

const ERROR_MESSAGE: Record<ErrorType, string> = {
  invalidDate: 'Please select a valid date',
  invalidRange: 'Please select a to date that is after the from date',
};

function computeActivityInvalidRange(val: TemplatableActivityDateRange): boolean {
  const isStartHolder = isTemplatePlaceholder(val.displayedStartDate);
  const isEndHolder = isTemplatePlaceholder(val.displayedEndDate);
  return Boolean(
    !isStartHolder &&
      !isEndHolder &&
      val.displayedStartDate &&
      val.displayedEndDate &&
      (val.displayedStartDate as Date) > (val.displayedEndDate as Date)
  );
}

const ActivityDateRangePicker = ({
  value,
  onChange,
  setIsFilterInvalid,
  isAdvancedSearch,
}: Props) => {
  const { displayedStartDate, displayedEndDate, periodDetails, periodValue } = value;

  const inSearchTemplate = useContext(SearchTemplatePageContext);
  const invalidRange = computeActivityInvalidRange(value);
  // to keep the error status consistent with DatePicker, we need to set the error status based on onError callback
  const [invalidFromDate, setInvalidFromDate] = useState(false);
  const [invalidToDate, setInvalidToDate] = useState(false);
  const invalidDate = invalidFromDate || invalidToDate;

  const invalidFromRef = useRef(false);
  const invalidToRef = useRef(false);
  // Keep period in a ref so deferred onError(null) from disabling the pickers
  // cannot clear sticky invalidity while Custom is inactive.
  const periodValueRef = useRef(periodValue);
  useEffect(() => {
    periodValueRef.current = periodValue;
  }, [periodValue]);

  const setInvalidFromForPicker = (next: boolean) => {
    invalidFromRef.current = next;
    setInvalidFromDate(next);
  };
  const setInvalidToForPicker = (next: boolean) => {
    invalidToRef.current = next;
    setInvalidToDate(next);
  };

  const reportValidity = (nextValue: TemplatableActivityDateRange) => {
    const dateFieldsActive = nextValue.periodValue === 'custom';
    setIsFilterInvalid(
      dateFieldsActive &&
        (invalidFromRef.current ||
          invalidToRef.current ||
          computeActivityInvalidRange(nextValue))
    );
  };

  const reportFieldInvalidity = (setField: (next: boolean) => void, isInvalid: boolean) => {
    // Disabling the pickers (leaving Custom) can fire onError(null); ignore those
    // clears so returning to Custom still sees the sticky invalid flags.
    if (periodValueRef.current !== 'custom' && !isInvalid) {
      return;
    }
    setField(isInvalid);
    reportValidity({
      ...value,
      periodValue: periodValueRef.current,
    });
  };

  // Parent can replace `value` without firing our onChange (e.g. reducer / preset). Keep Apply disabled state in sync.
  /* eslint-disable react-you-might-not-need-an-effect/no-pass-data-to-parent -- keep Apply disabled when parent replaces value */
  useEffect(() => {
    const dateFieldsActive = value.periodValue === 'custom';
    setIsFilterInvalid(
      dateFieldsActive &&
        (invalidFromRef.current || invalidToRef.current || computeActivityInvalidRange(value))
    );
  }, [value, setIsFilterInvalid]);

  const listValuesForDisplay = [
    { title: 'Last 2 days', value: 'last2days' },
    { title: 'Last week', value: 'lastweek' },
    { title: 'Last month', value: 'lastmonth' },
    { title: 'Last 3 months', value: 'last3months' },
    { title: 'Last 6 months', value: 'last6months' },
    { title: 'Last year', value: 'lastyear' },
  ];

  const dateFieldsActive = periodValue === 'custom';

  const errorMsg = useMemo(() => {
    if (!dateFieldsActive) return '';
    if (invalidDate) return ERROR_MESSAGE.invalidDate;
    if (invalidRange) return ERROR_MESSAGE.invalidRange;
    return '';
  }, [dateFieldsActive, invalidDate, invalidRange]);

  const changeHandler = ({
    type,
    payload,
  }: {
    type: 'startDate' | 'endDate' | 'periodValue' | 'periodDetails';
    payload: unknown;
  }) => {
    switch (type) {
      case 'startDate': {
        const next = {
          ...value,
          displayedStartDate: payload as Date | null,
        };
        onChange(next);
        reportValidity(next);
        break;
      }
      case 'endDate': {
        const next = {
          ...value,
          displayedEndDate: payload as Date | null,
        };
        onChange(next);
        reportValidity(next);
        break;
      }
      case 'periodValue': {
        const derivedPVPayload = payload as PeriodValueType;
        const next = {
          ...value,
          periodValue: derivedPVPayload,
        };
        // Update before onChange so disable-driven onError(null) sees the new period.
        periodValueRef.current = derivedPVPayload;
        // Keep from/to invalid flags while away from Custom so returning to Custom
        // re-applies them. reportValidity ignores them when date fields are inactive.
        onChange(next);
        reportValidity(next);
        break;
      }
      case 'periodDetails': {
        const derivedPDPayload = payload as PeriodDetailsType;
        const next = {
          ...value,
          periodDetails: derivedPDPayload,
        };
        onChange(next);
        reportValidity(next);
        break;
      }

      default:
        break;
    }
  };

  const fromField = useDeferredRangeDateField({
    value: displayedStartDate,
    reportInvalid: (isInvalid) => reportFieldInvalidity(setInvalidFromForPicker, isInvalid),
    commit: (newValue) => changeHandler({ type: 'startDate', payload: newValue }),
  });
  const toField = useDeferredRangeDateField({
    value: displayedEndDate,
    reportInvalid: (isInvalid) => reportFieldInvalidity(setInvalidToForPicker, isInvalid),
    commit: (newValue) => changeHandler({ type: 'endDate', payload: newValue }),
  });

  const handlePeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    changeHandler({ type: 'periodValue', payload: (event.target as HTMLInputElement).value });
  };

  const handleChangePeriodDetails = (selectValue: TemplatePlaceholder | string) => {
    changeHandler({
      type: 'periodDetails',
      payload: selectValue,
    });
  };

  const disableRatioButtons =
    isTemplatePlaceholder(periodDetails) ||
    isTemplatePlaceholder(value.displayedStartDate) ||
    isTemplatePlaceholder(value.displayedEndDate);

  return (
    <AccordionField title="In the period:">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            width: isAdvancedSearch ? '70ch' : '100%',
            minWidth: 0,
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            '& label': {
              paddingTop: '0.15rem',
            },
          }}
        >
          <FormControl>
            <RadioGroup
              aria-label="Engagement period"
              name="engagement-period-radio-buttons-group"
              value={periodValue}
              onChange={handlePeriodChange}
            >
              <ToolTipRadioWrapper value="anytime" disabled={disableRatioButtons} label="Anytime" />
              <ToolTipRadioWrapper
                value="bytimeperiod"
                disabled={disableRatioButtons}
                label="By time period"
              />
              <ToolTipRadioWrapper value="custom" disabled={disableRatioButtons} label="Custom" />
            </RadioGroup>
          </FormControl>
          <FormControl variant="filled" sx={{ flexShrink: 0 }}>
            <TemplateCapableSelect
              sx={{ width: '180px', marginTop: '38px' }}
              disabled={periodValue !== 'bytimeperiod'}
              isOnTemplatePage={inSearchTemplate}
              value={periodDetails}
              onChange={handleChangePeriodDetails}
              valueAfterRemovePlaceholder={listValuesForDisplay[1].value}
              templateBuilderDisplayName={'Engagement history filter: Time period'}
              data-testid="periodDetails"
            >
              {listValuesForDisplay.map((x) => (
                <MenuItem key={x.value} value={x.value}>
                  {x.title}
                </MenuItem>
              ))}
            </TemplateCapableSelect>
          </FormControl>
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            width: isAdvancedSearch ? '70ch' : '100%',
            minWidth: 0,
            justifyContent: 'space-between',
            pt: 2,
            'label:not(.Mui-focused)': {
              fontSize: '0.85rem',
              paddingTop: '0.15rem',
            },
          }}
        >
          <TemplateCapableDatePicker
            label="From date"
            sx={{ flex: 1, minWidth: 0 }}
            slotProps={{ textField: { fullWidth: true, ...fromField.textFieldSlotProps } }}
            value={fromField.displayValue}
            isOnTemplatePage={inSearchTemplate}
            templateBuilderDisplayName="Engagement history filter: From date"
            disabled={!dateFieldsActive}
            isError={dateFieldsActive && (invalidRange || invalidFromDate)}
            onError={fromField.handleError}
            onChange={fromField.handleChange}
          />
          <TemplateCapableDatePicker
            label="To date"
            sx={{ flex: 1, minWidth: 0 }}
            slotProps={{ textField: { fullWidth: true, ...toField.textFieldSlotProps } }}
            value={toField.displayValue}
            isOnTemplatePage={inSearchTemplate}
            templateBuilderDisplayName="Engagement history filter: To date"
            disabled={!dateFieldsActive}
            isError={dateFieldsActive && (invalidRange || invalidToDate)}
            onError={toField.handleError}
            onChange={toField.handleChange}
          />{' '}
        </Stack>
        {errorMsg && <FormHelperText className="Mui-error">{errorMsg}</FormHelperText>}
      </LocalizationProvider>
    </AccordionField>
  );
};

export default ActivityDateRangePicker;
